import React, { useState, useEffect } from 'react';
import {
    Grid,
    InputLabel,
    MenuItem,
    FormControl,
    Select
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { isAdmin } from '../../services/auth';
import { api } from '../../services/api';
import { createReport } from '../../apis/reports';
import { Navigate, useNavigate } from "react-router-dom";

import PageCreate from '../../components/pageCreate';
import FormCard from '../../components/forms/formCard';

export default function ReportsCreate() {
    const navigate = useNavigate();
    const [running, setRunning] = useState(false);
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState("");
    const [severity, setSeverity] = useState("success");

    const [tipo, setTipo] = useState("ACC");
    
    // Filtros ACC
    const [bancoId, setBancoId] = useState("all");
    const [dataContratoInicio, setDataContratoInicio] = useState(null);
    const [dataContratoFim, setDataContratoFim] = useState(null);
    
    // Filtros Vendas
    const [dataVendaInicio, setDataVendaInicio] = useState(null);
    const [dataVendaFim, setDataVendaFim] = useState(null);

    const [bankList, setBankList] = useState([]);

    useEffect(() => {
        let mounted = true;
        api.getFinInstitutions()
            .then(resp => {
                if (mounted && resp) {
                    setBankList(resp);
                }
            });
        return () => {
            mounted = false;
        }
    }, []);

    if (!isAdmin()) return <Navigate to="/dashboard" />;

    const handleSubmit = async e => {
        e.preventDefault();
        setRunning(true);
        
        const payload = {
            tipo: tipo,
            filtros: {}
        };

        if (tipo === 'ACC') {
            if (bancoId && bancoId !== "all") {
                payload.filtros.bancoId = bancoId;
                const bank = bankList.find(b => b.id === bancoId);
                if (bank) payload.filtros.bancoNome = bank.razaoSocial;
            }
            if (dataContratoInicio) payload.filtros.dataContratoInicio = dataContratoInicio.format('YYYY-MM-DD');
            if (dataContratoFim) payload.filtros.dataContratoFim = dataContratoFim.format('YYYY-MM-DD');
        } else {
            if (dataVendaInicio) payload.filtros.dataVendaInicio = dataVendaInicio.format('YYYY-MM-DD');
            if (dataVendaFim) payload.filtros.dataVendaFim = dataVendaFim.format('YYYY-MM-DD');
        }

        try {
            await createReport(payload);
            setMessage("Relatório solicitado com sucesso!");
            setSeverity("success");
            setOpen(true);
            setTimeout(() => {
                navigate("/reports");
            }, 1500);
        } catch (error) {
            setMessage("Erro ao solicitar relatório!");
            setSeverity("error");
            setOpen(true);
        } finally {
            setRunning(false);
        }
    }

    return (
        <PageCreate 
            title="Solicitar Novo Relatório" 
            handleSubmit={handleSubmit} 
            message={message} 
            severity={severity} 
            running={running} 
            setRunning={setRunning} 
            open={open} 
            setOpen={setOpen}
        >
            <Grid item xs={12} sm={12}>
                <FormCard title="Configuração do Relatório">
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="tipo-label">Tipo de Relatório</InputLabel>
                                <Select
                                    required
                                    labelId="tipo-label"
                                    id="tipo"
                                    value={tipo}
                                    label="Tipo de Relatório"
                                    onChange={event => setTipo(event.target.value)}
                                >
                                    <MenuItem value="ACC">ACC</MenuItem>
                                    <MenuItem value="VENDA">Vendas</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    {/* FILTROS ACC */}
                    {tipo === 'ACC' && (
                        <Grid container spacing={3} sx={{ mt: 1 }}>
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="banco-label">Banco</InputLabel>
                                    <Select
                                        labelId="banco-label"
                                        id="banco"
                                        value={bancoId}
                                        label="Banco"
                                        onChange={event => setBancoId(event.target.value)}
                                    >
                                        <MenuItem value="all">Todos</MenuItem>
                                        {bankList.map((field) => (
                                            <MenuItem key={field.id} value={field.id}>{field.razaoSocial}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        sx={{ width: '100%' }}
                                        label="Data Negociação (Início)"
                                        format="DD/MM/YYYY"
                                        value={dataContratoInicio}
                                        onChange={event => setDataContratoInicio(event)}
                                        slotProps={{ textField: { size: 'small' } }}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        sx={{ width: '100%' }}
                                        label="Data Negociação (Fim)"
                                        format="DD/MM/YYYY"
                                        value={dataContratoFim}
                                        onChange={event => setDataContratoFim(event)}
                                        slotProps={{ textField: { size: 'small' } }}
                                    />
                                </LocalizationProvider>
                            </Grid>
                        </Grid>
                    )}

                    {/* FILTROS VENDA */}
                    {tipo === 'VENDA' && (
                        <Grid container spacing={3} sx={{ mt: 1 }}>
                            <Grid item xs={12} sm={6}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        sx={{ width: '100%' }}
                                        label="Data Venda (Início)"
                                        format="DD/MM/YYYY"
                                        value={dataVendaInicio}
                                        onChange={event => setDataVendaInicio(event)}
                                        slotProps={{ textField: { size: 'small' } }}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        sx={{ width: '100%' }}
                                        label="Data Venda (Fim)"
                                        format="DD/MM/YYYY"
                                        value={dataVendaFim}
                                        onChange={event => setDataVendaFim(event)}
                                        slotProps={{ textField: { size: 'small' } }}
                                    />
                                </LocalizationProvider>
                            </Grid>
                        </Grid>
                    )}
                </FormCard>
            </Grid>
        </PageCreate>
    );
}
